/**
 * 운영자 「이용 내역」 화면 규칙 (W6 A2 — 계약 AUDIT 6-2·9, 설계 1-6·1-8, log.md 8-1·8-4).
 * 조회: POST /audit/events/search(회사 또는 사람 먼저, 기간 ≤92일, 커서, 검색어는 본문으로만).
 * Vue·axios 를 들이지 않는 순수 모듈이라 node --test 로 시험한다(`__tests__/auditLog.test.mjs`).
 */
import { appName, changeValueText, fieldName, formatName, plainActionName, reasonName, securitySettingName, targetTypeName } from "./auditLabels.js";

export const DEFAULT_DAYS = 7;
export const MAX_DAYS = 92;
export const PAGE_LIMIT = 50;
const DAY_MS = 24 * 60 * 60 * 1000;
const KST_MS = 9 * 60 * 60 * 1000;

/** 접속 방식 배지(계약 VIAS). 이행 기간에만 있는 셋은 「(이전 방식)」 */
export const VIA_LABEL = {
  self: "본인",
  operator_support: "서티라이프 지원",
  reseller_support: "리셀러 지원",
  customer_proxy: "환자 대리",
  api_key: "API 키",
  system: "시스템",
  reseller_acting: "리셀러 대행(이전 방식)",
  reseller_proxy: "리셀러 대리(이전 방식)",
  shared_password: "공용 비밀번호(이전 방식)",
};
export const LEGACY_VIAS = ["reseller_acting", "reseller_proxy", "shared_password"];

/** 소속 배지(계약 ACTOR_TYPES) */
export const ACTOR_TYPE_LABEL = {
  staff: "직원",
  operator: "서티라이프",
  reseller: "리셀러",
  unknown: "알 수 없음",
  api_key: "API 키",
  system: "시스템",
};

export const OUTCOME_LABEL = { success: "성공", failure: "실패", denied: "거부" };
/** 결과 필터: 전체 / 실패·거부 */
export const OUTCOME_FILTERS = [
  { value: "all", label: "전체", outcomes: undefined },
  { value: "bad", label: "실패·거부", outcomes: ["failure", "denied"] },
];

/** 내려받기 사유(계약 EXPORT_REASON_CODES, OTHER 는 메모 필수) */
export const EXPORT_REASONS = [
  { value: "CUSTOMER_REQUEST", label: "고객 요청" },
  { value: "REPORT", label: "보고" },
  { value: "MIGRATION", label: "이관" },
  { value: "AUDIT", label: "감사·점검" },
  { value: "OTHER", label: "기타" },
];

/** 기본 기간: 오늘(KST) 포함 7일. {from, to} 는 'YYYY-MM-DD'(KST 날짜) */
export const defaultRange = (now = Date.now()) => ({
  from: kstDate(now - (DEFAULT_DAYS - 1) * DAY_MS),
  to: kstDate(now),
});

/** epoch ms → KST 'YYYY-MM-DD' */
export const kstDate = (ms) => new Date(ms + KST_MS).toISOString().slice(0, 10);

/** 서버 UTC 시각 → KST 'YYYY-MM-DD HH:mm:ss'(저장은 UTC, 화면은 KST 초까지) */
export const formatKst = (iso) => {
  const t = Date.parse(iso);
  return Number.isNaN(t) ? "-" : new Date(t + KST_MS).toISOString().slice(0, 19).replace("T", " ");
};

/** KST 날짜 두 개 → 요청 칸(ISO UTC). to 는 그날 끝(다음 날 00:00 KST 직전까지) */
export const rangeToIso = ({ from, to }) => ({
  from: new Date(`${from}T00:00:00+09:00`).toISOString(),
  to: new Date(Date.parse(`${to}T00:00:00+09:00`) + DAY_MS - 1).toISOString(),
});

const days = ({ from, to }) => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS) + 1;
const isDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || "")) && !Number.isNaN(Date.parse(v));
const trimmed = (v) => String(v ?? "").trim();

/**
 * 검색 전 검사. 괜찮으면 "", 아니면 안내 문구.
 * f = { view:'company'|'actor'|'mine', companyId, actorId, from, to, … }
 * mine = 「내 지원 기록」(ops.support.edit 만 있는 「고객 지원」 역할, 계약 AUDIT 6-2 mine:true) — 회사·사람을 고르지 않음
 */
export const searchError = (f = {}) => {
  if (f.view !== "mine" && (f.view === "actor" ? !trimmed(f.actorId) : !trimmed(f.companyId)))
    return f.view === "actor" ? "사람을 먼저 고르세요." : "회사를 먼저 고르세요.";
  if (!isDate(f.from) || !isDate(f.to)) return "기간을 고르세요.";
  if (Date.parse(f.from) > Date.parse(f.to)) return "시작일이 끝일보다 늦습니다.";
  if (days(f) > MAX_DAYS) return `기간은 최대 ${MAX_DAYS}일까지 고를 수 있습니다.`;
  return "";
};

/**
 * POST /audit/events/search 본문. 빈 칸은 보내지 않는다.
 * 고객 번호·이메일·이름은 회사를 골랐을 때만(회사별 HMAC 키) subject 로 — URL 에 싣지 않는다(규칙 AUDIT 6).
 */
export const buildSearchBody = (f = {}, cursor) => {
  const body = rangeToIso(f);
  if (f.view === "mine") body.mine = true;
  else if (f.view === "actor") body.actorId = trimmed(f.actorId);
  else body.companyId = trimmed(f.companyId);
  const list = (v) => (Array.isArray(v) && v.length ? [...v] : undefined);
  const put = (k, v) => {
    if (v !== undefined && v !== "") body[k] = v;
  };
  put("categories", list(f.categories));
  put("actions", list(f.actions));
  put("actorTypes", list(f.actorTypes));
  put("outcomes", (OUTCOME_FILTERS.find((o) => o.value === f.outcome) || {}).outcomes);
  put("targetId", trimmed(f.targetId));
  put("sid", trimmed(f.sid));
  if (f.view === "company") {
    const subject = {};
    ["phone", "email", "name"].forEach((k) => {
      const v = trimmed(f.subject && f.subject[k]);
      if (v) subject[k] = k === "phone" ? v.replace(/\D/g, "") : v;
    });
    if (Object.keys(subject).length) body.subject = subject;
  }
  if (cursor) body.cursor = cursor;
  body.limit = Math.min(Number(f.limit) || PAGE_LIMIT, 100);
  return body;
};

/** 「더 보기」: 앞 쪽 뒤에 이어 붙임(같은 _id 는 한 번) */
export const appendPage = (prev = [], page = {}) => {
  const seen = new Set(prev.map((r) => r._id));
  const rows = [...prev, ...((page && page.rows) || []).filter((r) => r && !seen.has(r._id))];
  return { rows, nextCursor: (page && page.nextCursor) || null };
};

/** 서티라이프 운영자가 고객사 데이터를 운영 콘솔에서 본 행(DD-15): operator + self + 이벤트 회사 ≠ 운영자 소속 */
export const isOpsConsoleRow = (row = {}) =>
  row.actor?.type === "operator" &&
  (row.session?.via || "self") === "self" &&
  !!row.companyId &&
  String(row.companyId) !== String(row.actor?.companyId || "");

/** 「누가」 칸 배지 [소속][접속 방식] */
export const actorBadges = (row = {}) => {
  const badges = [];
  const type = row.actor?.type;
  if (type) badges.push({ text: ACTOR_TYPE_LABEL[type] || "기타", kind: "type" });
  if (isOpsConsoleRow(row)) badges.push({ text: "서티라이프 운영 화면", kind: "ops" });
  else {
    const via = row.session?.via;
    if (via) badges.push({ text: viaName(via), kind: LEGACY_VIAS.includes(via) ? "legacy" : via === "self" ? "self" : "via" });
  }
  return badges;
};

/** 접속 방식 이름(모르는 값 = 「기타」, 없으면 "") */
export const viaName = (via) => (via ? VIA_LABEL[via] || "기타" : "");

/** 행동 이름: 행의 ko → GET /audit/actions 의 ko(괄호 속 영문 코드 뺌) → 「기타 행동」(코드는 보이지 않음) */
export const actionLabel = (row = {}, dict = {}) =>
  plainActionName(row.ko) || plainActionName(dict.actions?.[row.action]?.ko) || "기타 행동";

/** 지원 접속 시작의 들어간 앱(meta.app — 계약 SUPPORT_APPS). 없으면 "" */
export const supportAppText = (meta) => appName(meta && meta.app);

/**
 * 대상 칸: 요청 경로 → 「요청 경로 · GET /admin/…」(경로는 추적용으로 그대로) → 단건 label(고객 가린 이름표 등)
 * → 목록·일괄 count → 「종류 이름 id」(보안 설정은 스위치 이름). 24자리 id 는 추적용으로 그대로
 */
export const targetText = (t) => {
  if (!t) return "-";
  if ((t.type === "route" || t.type === "admin_route") && (t.label || t.id)) return `${targetTypeName(t.type)} · ${t.label || t.id}`;
  if (t.label) return t.label;
  if (t.count != null) return `${t.count}건`;
  if (!t.id) return "-";
  if (t.type === "security_setting") return securitySettingName(t.id);
  return `${targetTypeName(t.type) || "대상"} ${t.id}`;
};

export const outcomeText = (row = {}) =>
  `${OUTCOME_LABEL[row.outcome] || (row.outcome ? "기타" : "-")}${row.reason ? ` (${reasonName(row.reason)})` : ""}`;

/** 접속 칸: 앱 이름 · IP(studio 와 같은 모양) */
export const sourceText = (row = {}) => [appName(row.source?.app), row.source?.ip].filter(Boolean).join(" · ") || "-";

const showValue = (v) => (v == null || v === "" ? "(없음)" : typeof v === "object" ? JSON.stringify(v) : String(v));

/**
 * 바뀐 칸(상세 changes = 계약 AuditChange[]) → [{field, text}]. 규칙은 studio(W5)와 같음(FOLLOWUP 10-27):
 * {f,b,a} 이전 → 이후 · {f,a} 새 값 · {f,b} 삭제됨(deletedForAudit) · {f,changed:true} 바뀜(값 숨김) · {f,added,removed} 더함·빠짐.
 * 값은 서버가 이미 가린 것(diffForAudit·deletedForAudit)만 온다. 권한 코드·모드 값은 쉬운 이름(permLabels = {code: ko})
 */
export const changeLines = (changes, permLabels = {}) => {
  if (!Array.isArray(changes)) return [];
  return changes.map((c = {}) => {
    const field = fieldName(c.f);
    const show = (v) => changeValueText(c.f, v, permLabels);
    if (c.changed) return { field, text: "바뀜(값 숨김)" };
    if (Array.isArray(c.added) || Array.isArray(c.removed))
      return { field, text: `더함 ${show(c.added || [])} · 빠짐 ${show(c.removed || [])}` };
    const hasB = "b" in c;
    const hasA = "a" in c;
    if (hasB && hasA) return { field, text: `${show(c.b)} → ${show(c.a)}` };
    if (hasA) return { field, text: `새 값: ${show(c.a)}` };
    if (hasB) return { field, text: `삭제됨: ${show(c.b)}` };
    return { field, text: "바뀜(값 숨김)" };
  });
};

/** 사유 코드 이름(내려받기·삭제·그 밖은 코드 그대로) + 메모 */
export const reasonText = (meta = {}) => {
  const code = meta && meta.reasonCode;
  const label = ([...EXPORT_REASONS, ...DELETE_REASONS].find((r) => r.value === code) || {}).label || code || "";
  return [label, meta && meta.reasonNote].filter(Boolean).join(" — ");
};

/** 지원 접속이 끝난 이유(support.session.end meta.endReason, 계약 SUPPORT_SESSION_END_CAUSES) */
const SUPPORT_END_CAUSE = {
  USER: "직접 종료",
  TIMEOUT: "최대 시간(8시간) 지남",
  IDLE: "30분 동안 사용하지 않음",
  CONSENT_REVOKED: "고객 지원을 위한 접근 허용이 꺼짐",
  LOCKED: "지원 계정 잠김",
  ACTOR_REMOVED: "들어간 사람의 계정·권한 없어짐",
  SWITCH_OFF: "지원 접속 스위치 꺼짐",
};
export const endReasonText = (meta = {}) => {
  const code = meta && meta.endReason;
  return code ? SUPPORT_END_CAUSE[code] || code : "";
};

const ISO_AT = /^\d{4}-\d{2}-\d{2}T[\d:.]+(Z|[+-]\d{2}:?\d{2})$/;

/**
 * 내려받기 행의 「어떤 조건으로 받았나」(계약 AUDIT 「변경·내려받기 기록 규칙」) → [{label, text}]. 내려받기 행이 아니면 [].
 * 조건(target.filter)은 서버가 maskFilterForAudit 로 가린 사본이라 그대로 보인다(시각만 KST 로).
 */
export const exportLines = (row = {}) => {
  const t = row.target || {};
  const m = row.meta || {};
  const isExport = String(row.action || "").split(".")[2] === "export" || t.filter != null || Array.isArray(m.columns);
  if (!isExport) return [];
  const lines = [];
  const filter = t.filter && typeof t.filter === "object" ? t.filter : null;
  if (filter) {
    const text = Object.entries(filter)
      .map(([k, v]) => `${k}: ${typeof v === "string" && ISO_AT.test(v) ? formatKst(v) : showValue(v)}`)
      .join(" · ");
    lines.push({ label: "조건", text: text || "(조건 없음)" });
  }
  if (t.count != null) lines.push({ label: "건수", text: `${t.count}건${m.rowCount != null && m.rowCount !== t.count ? ` (파일 ${m.rowCount}줄)` : ""}` });
  else if (m.rowCount != null) lines.push({ label: "건수", text: `파일 ${m.rowCount}줄` });
  if (Array.isArray(m.columns) && m.columns.length) lines.push({ label: "칸", text: m.columns.join(", ") });
  if (m.format) lines.push({ label: "형식", text: `${formatName(m.format)}${m.clientSide ? " (화면에서 만든 파일)" : ""}` });
  return lines;
};

/** 이용 내역 삭제(계약 AUDIT 「이용 내역 삭제」, ops.security.manage + 2차 인증 늘) */
export const DELETE_MAX = 500;
export const DELETE_REASONS = [
  { value: "WRONG_RECORD", label: "잘못 남은 기록" },
  { value: "TEST_DATA", label: "시험 데이터" },
  { value: "LEGAL_REQUEST", label: "법적 요청" },
  { value: "OTHER", label: "기타" },
];
/** 서버가 지우지 않는 행(audit.*·system.*) — 고르기 칸을 막음(판정은 서버) */
export const isProtectedRow = (row = {}) => /^(audit|system)\./.test(String(row.action || ""));

export const deleteError = (ids, reasonCode, reasonNote) => {
  if (!Array.isArray(ids) || !ids.length) return "지울 기록을 고르세요.";
  if (ids.length > DELETE_MAX) return `한 번에 ${DELETE_MAX}건까지 지울 수 있습니다(지금 ${ids.length}건).`;
  if (!DELETE_REASONS.some((r) => r.value === reasonCode)) return "지우는 사유를 고르세요.";
  if (reasonCode === "OTHER" && !trimmed(reasonNote)) return "기타 사유는 메모를 적어 주세요.";
  return "";
};

/** POST /admin/audit/events/delete 본문(빈 메모는 보내지 않음) */
export const buildDeleteBody = (ids, reasonCode, reasonNote, mfaToken) => {
  const body = { ids: [...ids], reasonCode, mfaToken };
  const note = trimmed(reasonNote);
  if (note) body.reasonNote = note;
  return body;
};

/** 응답 {deleted, protected, notFound} → 안내 문구 */
export const deleteResultText = (res = {}) => {
  const kept = (res.protected || []).length;
  const missing = (res.notFound || []).length;
  return [
    `${Number(res.deleted) || 0}건을 지웠습니다.`,
    kept ? `이용 내역·시스템 기록 ${kept}건은 지울 수 없어 남겼습니다.` : "",
    missing ? `${missing}건은 이미 없습니다.` : "",
    // 매일 보관 사본(WORM)은 만들지 않음(사용자 결정) — 지우면 다른 곳에 남지 않는다
    "지운 기록은 검색과 내려받기에서 사라집니다. 지운 사실과 사유는 이용 내역에 남습니다.",
  ]
    .filter(Boolean)
    .join(" ");
};

/** 목록에서 뺄 id = 보낸 것 − 보호된 것(없던 것도 뺌) */
export const removeDeleted = (rows = [], ids = [], res = {}) => {
  const kept = new Set(res.protected || []);
  const gone = new Set(ids.filter((id) => !kept.has(id)));
  return rows.filter((r) => !gone.has(r._id));
};

export const NEEDS_FACTOR_TEXT = "이용 내역을 지우려면 2차 인증 수단이 필요합니다(2차 인증 예외로는 지울 수 없음). 「내 계정 보안」에서 수단을 먼저 등록하세요.";
// 403 MFA_REQUIRED + reason(계약 AUDIT 「이용 내역 삭제」)
const DELETE_MFA_TEXT = {
  NO_FACTOR: NEEDS_FACTOR_TEXT,
  EXEMPT: NEEDS_FACTOR_TEXT,
  STALE: "인증한 지 5분이 지났습니다. 인증번호를 다시 받아 확인하세요.",
  INVALID: "본인 확인이 맞지 않습니다. 인증번호를 다시 받아 확인하세요.",
  NO_TOKEN: "본인 확인이 반영되지 않았습니다. 인증번호를 다시 받아 확인하세요.",
};
/** 삭제 실패 중 2차 인증 문구. 해당 없으면 null(공통 문구로) */
export const deleteMfaText = (error) => {
  const d = (error && error.response && error.response.data) || {};
  if (d.code !== "MFA_REQUIRED") return null;
  return DELETE_MFA_TEXT[d.reason] || DELETE_MFA_TEXT.NO_TOKEN;
};

/** 내려받기 사유 검사 */
export const exportReasonError = (reasonCode, reasonNote) => {
  if (!EXPORT_REASONS.some((r) => r.value === reasonCode)) return "내려받는 사유를 고르세요.";
  if (reasonCode === "OTHER" && !trimmed(reasonNote)) return "기타 사유는 메모를 적어 주세요.";
  return "";
};

/** POST /audit/events/export 본문 = 검색 조건(커서·쪽 크기 빼고) + 사유 */
export const buildExportBody = (f, reasonCode, reasonNote) => {
  const body = buildSearchBody(f);
  delete body.cursor;
  delete body.limit;
  body.reasonCode = reasonCode;
  const note = trimmed(reasonNote);
  if (note) body.reasonNote = note;
  return body;
};

/** 행동 고르기 목록(분류를 골랐으면 그 분류만). dict = GET /audit/actions */
export const actionOptions = (dict = {}, categories = []) =>
  Object.entries(dict.actions || {})
    .filter(([, a]) => !categories.length || categories.includes(a.category))
    .map(([value, a]) => ({ value, label: `${a.ko || value} (${value})` }))
    .sort((a, b) => a.value.localeCompare(b.value));
